import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormHelperText,
  TextField,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Autocomplete } from '@mui/material';
import { linkCropToIntegrator } from './crops.service';
import apiEndPoints from "../../routeConfig";
import { ApiService } from "../../../service/api.service";

const apiService = new ApiService();

export default function BulkLinkModal({ open, selectedCropIds, selectedCrops = [], onClose, onSuccess }) {
  const [ecosystemIntegrators, setEcosystemIntegrators] = useState([]);
  const [selectedIntegrators, setSelectedIntegrators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingIntegrators, setFetchingIntegrators] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      fetchEcosystemIntegrators();
    }
  }, [open]);

  const fetchEcosystemIntegrators = async () => {
    setFetchingIntegrators(true);
    try {
      const response = await apiService.get(`${apiEndPoints.getAllEcosytemIntegator}`);
      const integrators = Array.isArray(response?.data?.content) ? response.data.content : response?.data || [];
      setEcosystemIntegrators(integrators);
    } catch (err) {
      setError('Failed to load ecosystem integrators');
      console.error(err);
    } finally {
      setFetchingIntegrators(false);
    }
  };

  const handleLinkCrops = async () => {
    if (selectedIntegrators.length === 0) {
      setError('Please select at least one ecosystem integrator');
      return;
    }

    setLoading(true);
    setError('');
    let successCount = 0;
    let failureCount = 0;

    try {
      for (const cropId of selectedCropIds) {
        for (const integrator of selectedIntegrators) {
          try {
            await linkCropToIntegrator(cropId, integrator.id);
            successCount++;
          } catch (err) {
            failureCount++;
          }
        }
      }

      if (failureCount === 0) {
        onSuccess(`Successfully linked ${successCount} crop-integrator pairs`);
      } else {
        onSuccess(
          `Linked ${successCount} pairs. ${failureCount} pairs failed.`,
          'warning'
        );
      }
      handleClose();
    } catch (err) {
      setError('An error occurred while linking crops');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedIntegrators([]);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Link Crops to Ecosystem Integrators</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Selected Crops ({selectedCropIds.length})
          </Typography>
          <Typography variant="body2" sx={{ color: '#625B71', fontSize: 13 }}>
            {selectedCrops.length > 0 ? selectedCrops.map(crop => crop.name).join(', ') : 'None selected'}
          </Typography>
        </Box>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
            Select Ecosystem Integrators *
          </Typography>
          {fetchingIntegrators ? (
            <CircularProgress size={24} />
          ) : (
            <Autocomplete
              multiple
              options={ecosystemIntegrators}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.integratorName || option.name || option.id;
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedIntegrators}
              onChange={(e, value) => {
                console.log('Selected integrators:', value);
                setSelectedIntegrators(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search and select integrators..."
                />
              )}
              disabled={loading}
              noOptionsText="No ecosystem integrators available"
            />
          )}
          {selectedIntegrators.length === 0 && (
            <FormHelperText>Select at least one ecosystem integrator</FormHelperText>
          )}
          {selectedIntegrators.length > 0 && (
            <Typography variant="caption" sx={{ color: '#625B71', fontSize: 12, mt: 1, display: 'block' }}>
              Selected: {selectedIntegrators.map(int => int.integratorName || int.name || int.id).join(', ')}
            </Typography>
          )}
        </FormControl>

        <Typography variant="body2" sx={{ color: '#625B71', mt: 2 }}>
          This will link all {selectedCropIds.length} selected crops to the selected integrators.
          {selectedIntegrators.length > 0 && (
            <>
              {' '}
              Total links to be created: {selectedCropIds.length * selectedIntegrators.length}
            </>
          )}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleLinkCrops}
          variant="contained"
          disabled={loading || selectedIntegrators.length === 0 || selectedCropIds.length === 0}
        >
          {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Link Crops
        </Button>
      </DialogActions>
    </Dialog>
  );
}
